import React, { useState, useEffect, useCallback } from 'react';
import { ToastProvider, useToast } from '../components/Toast';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => Number(n || 0).toLocaleString('en-IN');

// ─── Status Badge ─────────────────────────────────────────────────────────────
function Badge({ label, color, bg }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, color, background: bg, display: 'inline-block' }}>
      {label}
    </span>
  );
}

// ─── Pipeline Progress ────────────────────────────────────────────────────────
function Pipeline({ status, darkMode, T }) {
  const steps = ['Lead', 'Task', 'Invoiced', 'Paid', 'Commission'];
  const idx = { lead: 0, task: 1, invoiced: 2, paid: 3, commission_earned: 4 };
  const current = idx[status] ?? 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: i <= current ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : T.bg3,
              border: `2px solid ${i <= current ? '#1a3a6b' : T.border}`,
              transition: 'all 0.3s',
            }}>
              {i < current ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
              ) : (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === current ? '#fff' : T.text3 }} />
              )}
            </div>
            <span style={{ fontSize: 9, color: i <= current ? '#3b82f6' : T.text3, fontWeight: i === current ? 700 : 400, whiteSpace: 'nowrap' }}>{step}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 2, background: i < current ? 'linear-gradient(90deg,#0f1f45,#1a3a6b)' : T.border, minWidth: 20, marginBottom: 16 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Partner Card ─────────────────────────────────────────────────────────────
function PartnerCard({ partner, onView, onRedeem, onEncash, darkMode, T }) {
  const isClient = partner.partner_type === 'client';
  const netPayable = isClient ? Math.max(0, (partner.ledger_outstanding || 0) - (partner.credit_balance || 0)) : null;

  return (
    <div style={{
      background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 16, overflow: 'hidden',
      boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)',
      transition: 'box-shadow 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = darkMode ? '0 8px 30px rgba(0,0,0,0.4)' : '0 8px 24px rgba(15,31,69,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)'}>

      {/* Header */}
      <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700 }}>
            {(partner.name || 'P').charAt(0).toUpperCase()}
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>{partner.name}</p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', margin: '2px 0 0' }}>{partner.referral_partner_id}</p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: partner.status === 'active' ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.1)', color: partner.status === 'active' ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>
            {partner.status === 'active' ? '● Active' : '○ Inactive'}
          </span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.1)', padding: '2px 7px', borderRadius: 10 }}>
            {isClient ? 'Client Partner' : 'External Partner'}
          </span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px' }}>
        {/* Commission rate */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, padding: '8px 12px', background: T.bg3, borderRadius: 8 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
          <span style={{ fontSize: 12, color: T.text2, fontWeight: 500 }}>
            Commission: {partner.commission_type === 'percentage' ? `${partner.commission_value}%` : fmt(partner.commission_value)} per referral
          </span>
        </div>

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: isClient ? '1fr 1fr' : '1fr 1fr', gap: 10, marginBottom: 14 }}>

          {/* Ledger Balance — only for client partners */}
          {isClient && (
            <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
              <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Ledger Balance</p>
              <p style={{ fontSize: 16, fontWeight: 800, color: (partner.ledger_outstanding || 0) > 0 ? '#dc2626' : '#16a34a', margin: 0 }}>
                {fmt(partner.ledger_outstanding || 0)}
              </p>
              <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>outstanding</p>
            </div>
          )}

          {/* Referral Credits */}
          <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Credits Earned</p>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#16a34a', margin: 0 }}>{fmt(partner.credit_balance || 0)}</p>
            <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>available</p>
          </div>

          {/* Total earned */}
          <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Total Earned</p>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#3b82f6', margin: 0 }}>{fmt(partner.total_earned || 0)}</p>
            <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>lifetime</p>
          </div>

          {/* Leads count */}
          <div style={{ background: T.bg3, borderRadius: 10, padding: '10px 12px', border: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px' }}>Leads Referred</p>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#8b5cf6', margin: 0 }}>{fmtN(partner.total_leads || 0)}</p>
            <p style={{ fontSize: 10, color: T.text3, margin: '3px 0 0' }}>{partner.converted_leads || 0} converted</p>
          </div>
        </div>

        {/* Net Payable — only for client partners */}
        {isClient && (partner.ledger_outstanding || 0) > 0 && (partner.credit_balance || 0) > 0 && (
          <div style={{ background: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', border: `1px solid ${darkMode ? 'rgba(59,130,246,0.25)' : '#bfdbfe'}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>Net Payable (after credit)</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#1d4ed8' }}>{fmt(netPayable)}</span>
            </div>
            <p style={{ fontSize: 11, color: T.text3, margin: '4px 0 0' }}>{fmt(partner.ledger_outstanding)} − {fmt(Math.min(partner.credit_balance, partner.ledger_outstanding))} credit</p>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onView(partner)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = T.bg3}
            onMouseLeave={e => e.currentTarget.style.background = T.bg2}>
            View Leads
          </button>
          {isClient && (partner.credit_balance || 0) > 0 && (
            <button onClick={() => onRedeem(partner)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Redeem
            </button>
          )}
          {(partner.credit_balance || 0) > 0 && (
            <button onClick={() => onEncash(partner)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Encash
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Partner Leads Modal ──────────────────────────────────────────────────────
function PartnerLeadsModal({ partner, onClose, darkMode, T }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${BASE}/api/referral-partners/${partner.id}/leads`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json())
      .then(d => setLeads(Array.isArray(d) ? d : []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, [partner.id, token]);

  const statusColor = (s) => ({
    lead: '#3b82f6', task: '#8b5cf6', invoiced: '#f59e0b',
    paid: '#16a34a', commission_earned: '#f97316'
  }[s] || '#94a3b8');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Leads by {partner.name}</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '3px 0 0' }}>{partner.referral_partner_id} · {leads.length} referrals</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, scrollbarWidth: 'none' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: T.text3 }}>Loading...</div>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: T.bg3, border: `2px dashed ${T.border2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              </div>
              <p style={{ fontSize: 14, fontWeight: 600, color: T.text, margin: 0 }}>No referrals yet</p>
              <p style={{ fontSize: 12, color: T.text3, marginTop: 6 }}>Leads referred by this partner will appear here</p>
            </div>
          ) : leads.map((lead, i) => (
            <div key={lead.id} style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: 0 }}>{lead.lead_name || lead.contact_person || lead.company_name || 'Unknown'}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>Referred {lead.referral_date ? new Date(lead.referral_date).toLocaleDateString('en-IN') : '-'}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, color: statusColor(lead.status), background: `${statusColor(lead.status)}22` }}>
                    {lead.status?.replace(/_/g, ' ') || 'lead'}
                  </span>
                  {lead.commission_amount > 0 && (
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', margin: '4px 0 0' }}>
                      {lead.commission_status === 'earned' ? '✓ ' : '⏳ '}{fmt(lead.commission_amount)}
                    </p>
                  )}
                </div>
              </div>
              <Pipeline status={lead.status} darkMode={darkMode} T={T} />
              {lead.invoice_amount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 11, color: T.text3 }}>Invoice: <strong style={{ color: T.text }}>{fmt(lead.invoice_amount)}</strong></span>
                  <span style={{ fontSize: 11, color: T.text3 }}>Expected Commission: <strong style={{ color: '#f97316' }}>{fmt(lead.commission_amount)}</strong></span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Redeem Modal ─────────────────────────────────────────────────────────────
function RedeemModal({ partner, onClose, onSuccess, darkMode, T }) {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${BASE}/api/clients/${partner.client_id}/invoices?status=unpaid`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json())
      .then(d => setInvoices(Array.isArray(d) ? d : []))
      .catch(() => setInvoices([]));
  }, [partner.client_id, token]);

  const handleRedeem = async () => {
    if (!selectedInvoice || !amount) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners/${partner.id}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoice_id: selectedInvoice, amount: Number(amount) })
      });
      onSuccess();
      onClose();
    } catch (_) {}
    setSaving(false);
  };

  const is = { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#16a34a,#15803d)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Redeem Credits</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: '3px 0 0' }}>Available: {fmt(partner.credit_balance)}</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding: '20px 24px', background: T.bg3, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Select Invoice to Adjust</label>
            <select value={selectedInvoice} onChange={e => setSelectedInvoice(e.target.value)} style={is}>
              <option value="">Select pending invoice...</option>
              {invoices.map(inv => (
                <option key={inv.id} value={inv.id}>{inv.invoice_number} — {fmt(inv.outstanding_amount)} outstanding</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Amount to Redeem</label>
            <input type="number" min={1} max={partner.credit_balance} value={amount} onChange={e => setAmount(e.target.value)} placeholder={`Max ${fmt(partner.credit_balance)}`} style={is} />
          </div>
          {selectedInvoice && amount && (
            <div style={{ background: darkMode ? 'rgba(22,163,74,0.1)' : '#f0fdf4', border: `1px solid ${darkMode ? 'rgba(22,163,74,0.25)' : '#bbf7d0'}`, borderRadius: 10, padding: '12px 14px' }}>
              <p style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, margin: 0 }}>
                Ledger entry: −{fmt(amount)} credit applied against invoice
              </p>
              <p style={{ fontSize: 11, color: T.text3, margin: '4px 0 0' }}>No Credit Note issued. GST-safe contra settlement.</p>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleRedeem} disabled={saving || !selectedInvoice || !amount} style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!selectedInvoice || !amount || saving) ? 0.6 : 1 }}>
            {saving ? 'Processing...' : 'Apply Credit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Encash Modal ─────────────────────────────────────────────────────────────
function EncashModal({ partner, onClose, onSuccess, darkMode, T }) {
  const [form, setForm] = useState({ amount: '', bank_name: '', account_number: '', ifsc: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const quarters = () => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const labels = ['Q1 (Apr-Jun)', 'Q2 (Jul-Sep)', 'Q3 (Oct-Dec)', 'Q4 (Jan-Mar)'];
    const ends = ['30 Jun', '30 Sep', '31 Dec', '31 Mar'];
    return { label: labels[q], end: ends[q], year: now.getFullYear() };
  };
  const qtr = quarters();

  const handleEncash = async () => {
    if (!form.amount) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners/${partner.id}/encash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, amount: Number(form.amount), quarter: qtr.label })
      });
      onSuccess();
      onClose();
    } catch (_) {}
    setSaving(false);
  };

  const is = { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#f97316,#ea580c)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Encash Credits</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: '3px 0 0' }}>{qtr.label} · Cycle ends {qtr.end} {qtr.year}</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding: '20px 24px', background: T.bg3, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: darkMode ? 'rgba(249,115,22,0.1)' : '#fff7ed', border: `1px solid ${darkMode ? 'rgba(249,115,22,0.25)' : '#fed7aa'}`, borderRadius: 10, padding: '10px 14px' }}>
            <p style={{ fontSize: 12, color: '#f97316', fontWeight: 600, margin: 0 }}>Available Balance: {fmt(partner.credit_balance)}</p>
            <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>TDS will be deducted if annual payout exceeds ₹15,000</p>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Amount to Encash *</label>
            <input type="number" min={1} max={partner.credit_balance} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder={`Max ${fmt(partner.credit_balance)}`} style={is} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Bank Name</label>
              <input type="text" value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="e.g. SBI" style={is} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>IFSC Code</label>
              <input type="text" value={form.ifsc} onChange={e => setForm(f => ({ ...f, ifsc: e.target.value.toUpperCase() }))} placeholder="e.g. SBIN0001234" style={is} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Account Number</label>
            <input type="text" value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))} placeholder="Bank account number" style={is} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" style={is} />
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleEncash} disabled={saving || !form.amount} style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!form.amount || saving) ? 0.6 : 1 }}>
            {saving ? 'Processing...' : 'Request Encash'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add External Partner Modal ───────────────────────────────────────────────
function AddPartnerModal({ onClose, onSuccess, darkMode, T }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', gstn: '', commission_type: 'percentage', commission_value: 10, commission_on: 'all', notes: '' });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, partner_type: 'external' })
      });
      onSuccess();
      onClose();
    } catch (_) {}
    setSaving(false);
  };

  const is = { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: T.bg2, borderRadius: 20, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Add External Partner</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '3px 0 0' }}>Non-client referral partner</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: T.bg3, display: 'flex', flexDirection: 'column', gap: 14, scrollbarWidth: 'none' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Full Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Partner name or firm" style={is} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Phone</label>
              <input type="text" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Mobile number" style={is} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Email</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email address" style={is} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>GSTN (if registered)</label>
              <input type="text" value={form.gstn} onChange={e => setForm(f => ({ ...f, gstn: e.target.value.toUpperCase() }))} placeholder="GST number (optional)" style={is} />
            </div>
          </div>

          {/* Commission settings */}
          <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: T.text, margin: '0 0 14px' }}>Commission Settings</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Type</label>
                <select value={form.commission_type} onChange={e => setForm(f => ({ ...f, commission_type: e.target.value }))} style={is}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>
                  {form.commission_type === 'percentage' ? 'Rate (%)' : 'Amount (₹)'}
                </label>
                <input type="number" min={0} value={form.commission_value} onChange={e => setForm(f => ({ ...f, commission_value: Number(e.target.value) }))} style={is} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Applies On</label>
                <select value={form.commission_on} onChange={e => setForm(f => ({ ...f, commission_on: e.target.value }))} style={is}>
                  <option value="all">All Services</option>
                  <option value="specific">Specific Services Only</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: 'uppercase' }}>Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes" style={is} />
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name} style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!form.name || saving) ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Add Partner'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Performance Tab ──────────────────────────────────────────────────────────
function PerformanceTab({ partners, darkMode, T }) {
  const sorted = [...partners].sort((a, b) => (b.total_earned || 0) - (a.total_earned || 0));
  const maxRevenue = Math.max(...partners.map(p => p.revenue_generated || 0), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        {[
          { label: 'Total Partners', value: fmtN(partners.length), color: '#3b82f6' },
          { label: 'Active Partners', value: fmtN(partners.filter(p => p.status === 'active').length), color: '#16a34a' },
          { label: 'Total Commissions', value: fmt(partners.reduce((s, p) => s + (Number(p.total_earned) || 0), 0)), color: '#f97316' },
          { label: 'Revenue via Referral', value: fmt(partners.reduce((s, p) => s + (p.revenue_generated || 0), 0)), color: '#8b5cf6' },
        ].map((stat, i) => (
          <div key={i} style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '16px 20px', boxShadow: darkMode ? '0 4px 16px rgba(0,0,0,0.25)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
            <p style={{ fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', margin: '0 0 8px' }}>{stat.label}</p>
            <p style={{ fontSize: 20, fontWeight: 800, color: stat.color, margin: 0 }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 16, overflow: 'hidden', boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)' }}>
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Partner Leaderboard</p>
          <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>Ranked by commission earned</p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
            <tr>
              {['#', 'Partner', 'Type', 'Leads', 'Converted', 'Revenue', 'Commission', 'Balance'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '48px 16px', textAlign: 'center', color: T.text3, fontSize: 13 }}>No partners yet</td></tr>
            ) : sorted.map((p, i) => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${T.border}`, background: darkMode ? (i % 2 === 0 ? T.bg2 : T.bg3) : (i % 2 === 0 ? '#fff' : '#fafbff'), transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1e3a5f' : '#f0f4ff'}
                onMouseLeave={e => e.currentTarget.style.background = darkMode ? (i % 2 === 0 ? T.bg2 : T.bg3) : (i % 2 === 0 ? '#fff' : '#fafbff')}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: i < 3 ? ['#f59e0b', '#9ca3af', '#b45309'][i] : T.bg3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: i < 3 ? '#fff' : T.text3 }}>
                    {i + 1}
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0 }}>{p.name}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>{p.referral_partner_id}</p>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <Badge label={p.partner_type === 'client' ? 'Client' : 'External'} color={p.partner_type === 'client' ? '#3b82f6' : '#8b5cf6'} bg={p.partner_type === 'client' ? (darkMode ? 'rgba(59,130,246,0.12)' : '#eff6ff') : (darkMode ? 'rgba(139,92,246,0.12)' : '#f5f3ff')} />
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: T.text2, fontWeight: 600 }}>{fmtN(p.total_leads || 0)}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>{fmtN(p.converted_leads || 0)}</span>
                  <span style={{ fontSize: 11, color: T.text3 }}> ({(p.total_leads && p.converted_leads) ? Math.round((Number(p.converted_leads) / Number(p.total_leads)) * 100) : 0}%)</span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 60, height: 6, borderRadius: 3, background: T.bg3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: '#8b5cf6', width: `${((p.revenue_generated || 0) / maxRevenue) * 100}%`, borderRadius: 3, transition: 'width 0.4s' }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#8b5cf6' }}>{fmt(p.revenue_generated || 0)}</span>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: '#f97316' }}>{fmt(p.total_earned || 0)}</td>
                <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: (p.credit_balance || 0) > 0 ? '#16a34a' : T.text3 }}>{fmt(p.credit_balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MAIN MODULE ──────────────────────────────────────────────────────────────
// ─── Commissions Tab ──────────────────────────────────────────────────────────
function CommissionsTab({ commissions, partners, onRefresh, onManualAdd, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast, showConfirm } = useToast();
  const [approving, setApproving] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  const handleApprove = async (commission) => {
    setApproving(commission.id);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/commissions/${commission.id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (res.ok) { showToast('Commission approved and credited', 'success'); onRefresh(); }
      else { const d = await res.json().catch(() => ({})); showToast(d.message || 'Could not approve commission', 'error'); }
    } catch (_) { showToast('Server error while approving commission', 'error'); }
    setApproving(null);
  };

  const handleReject = async (commission) => {
    if (!(await showConfirm('Reject this commission?', { confirmLabel: 'Reject', confirmColor: '#ef4444' }))) return;
    try {
      await fetch(`${BASE}/api/referral-partners/commissions/${commission.id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      showToast('Commission rejected', 'success');
      onRefresh();
    } catch (_) { showToast('Server error while rejecting commission', 'error'); }
  };

  const handleEdit = async () => {
    if (!editAmount) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners/commissions/${editModal.id}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ commission_amount: editAmount, notes: editNote }),
      });
      showToast('Commission override saved', 'success');
      setEditModal(null);
      onRefresh();
    } catch (_) { showToast('Server error while saving override', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!(await showConfirm('Delete this commission entry? This cannot be undone.', { confirmLabel: 'Delete', confirmColor: '#ef4444' }))) return;
    try {
      await fetch(`${BASE}/api/referral-partners/commissions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Commission entry deleted', 'success');
      onRefresh();
    } catch (_) { showToast('Server error while deleting commission', 'error'); }
  };

  const statusColor = (s) => ({
    pending:  { color:'#f59e0b', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb',  border: darkMode?'rgba(245,158,11,0.3)':'#fde68a'  },
    approved: { color:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4',  border: darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'   },
    rejected: { color:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2',  border: darkMode?'rgba(220,38,38,0.3)':'#fecaca'   },
    manual:   { color:'#8b5cf6', bg: darkMode?'rgba(139,92,246,0.12)':'#f5f3ff', border: darkMode?'rgba(139,92,246,0.3)':'#ddd6fe'  },
  }[s] || { color:'#94a3b8', bg:T.bg3, border:T.border });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Edit Modal */}
      {editModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:420, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Edit Commission</h3>
              <button onClick={()=>setEditModal(null)} style={{ width:28, height:28, borderRadius:7, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase' }}>Commission Amount (₹) *</label>
                <input type="number" value={editAmount} onChange={e=>setEditAmount(e.target.value)} style={is} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase' }}>Override Note</label>
                <input type="text" value={editNote} onChange={e=>setEditNote(e.target.value)} placeholder="Reason for edit (admin override)" style={is} />
              </div>
              <div style={{ background:darkMode?'rgba(249,115,22,0.1)':'#fff7ed', border:`1px solid ${darkMode?'rgba(249,115,22,0.25)':'#fed7aa'}`, borderRadius:9, padding:'10px 14px' }}>
                <p style={{ fontSize:12, color:'#f97316', margin:0, fontWeight:600 }}>⚠ Admin Override — credit balance will be adjusted automatically</p>
              </div>
            </div>
            <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setEditModal(null)} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleEdit} disabled={saving||!editAmount} style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:(saving||!editAmount)?0.6:1 }}>
                {saving?'Saving...':'Save Override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Commission Approvals</p>
          <p style={{ fontSize:12, color:T.text3, margin:'3px 0 0' }}>{commissions.filter(c=>c.status==='pending').length} pending · {commissions.length} total</p>
        </div>
        <button onClick={() => onManualAdd(partners[0] || {})}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Manual Credit
        </button>
      </div>

      {/* Commission list */}
      {commissions.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}` }}>
          <div style={{ width:52, height:52, borderRadius:14, background:T.bg3, border:`2px dashed ${T.border2}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
          </div>
          <p style={{ fontSize:14, fontWeight:600, color:T.text, margin:0 }}>No commissions yet</p>
          <p style={{ fontSize:12, color:T.text3, marginTop:6 }}>Commissions appear here when referred tasks are fully paid</p>
        </div>
      ) : (
        <div style={{ background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow:darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Partner','Type','Service / Reason','Invoice Amt','Commission','Status','Actions'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {commissions.map((c, i) => {
                const sc = statusColor(c.status);
                const isPending = c.status === 'pending';
                return (
                  <tr key={c.id} style={{ borderBottom:`1px solid ${T.border}`, background:darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                    <td style={{ padding:'12px 16px' }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{c.partner_name}</p>
                      <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{c.referral_partner_id}</p>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <Badge label={c.commission_source === 'manual' ? 'Manual' : 'Auto'} color={c.commission_source==='manual'?'#8b5cf6':'#3b82f6'} bg={c.commission_source==='manual'?(darkMode?'rgba(139,92,246,0.12)':'#f5f3ff'):(darkMode?'rgba(59,130,246,0.12)':'#eff6ff')} />
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:12, color:T.text2, maxWidth:160 }}>
                      <p style={{ margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.service_name || c.reason || '—'}</p>
                      {c.notes && <p style={{ margin:'2px 0 0', fontSize:11, color:T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.notes}</p>}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, color:T.text2 }}>{c.invoice_amount ? fmt(c.invoice_amount) : '—'}</td>
                    <td style={{ padding:'12px 16px', fontSize:14, fontWeight:700, color:'#f97316' }}>{fmt(c.commission_amount)}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, color:sc.color, background:sc.bg, border:`1px solid ${sc.border}`, textTransform:'capitalize' }}>{c.status}</span>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                        {isPending && (
                          <>
                            <button onClick={() => handleApprove(c)} disabled={approving===c.id}
                              style={{ padding:'5px 12px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', opacity:approving===c.id?0.6:1 }}>
                              {approving===c.id ? '...' : 'Approve'}
                            </button>
                            <button onClick={() => handleReject(c)}
                              style={{ padding:'5px 10px', borderRadius:7, border:`1px solid ${darkMode?'rgba(220,38,38,0.3)':'#fecaca'}`, background:'none', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                              Reject
                            </button>
                          </>
                        )}
                        {/* Admin Override — Edit & Delete */}
                        <button onClick={() => { setEditModal(c); setEditAmount(c.commission_amount); setEditNote(''); }}
                          title="Edit (Admin Override)"
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                          onMouseEnter={e=>{e.currentTarget.style.background=T.bg3;e.currentTarget.style.color='#f97316';e.currentTarget.style.borderColor='#f97316';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=T.text3;e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button onClick={() => handleDelete(c.id)}
                          title="Delete (Admin Override)"
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                          onMouseEnter={e=>{e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2';e.currentTarget.style.borderColor='#ef4444';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Manual Commission Modal ───────────────────────────────────────────────────
function ManualCommissionModal({ partner: initialPartner, onClose, onSuccess, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({
    partner_id: initialPartner?.id || '',
    commission_amount: '',
    reason: '',
    notes: '',
    invoice_ref: '',
  });
  const [saving, setSaving] = useState(false);
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  useEffect(() => {
    fetch(`${BASE}/api/referral-partners?status=active`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r=>r.json()).then(d=>setPartners(Array.isArray(d)?d:[])).catch(()=>{});
  }, [token]);

  const handleSave = async () => {
    if (!form.partner_id || !form.commission_amount || !form.reason) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners/commissions/manual`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify(form),
      });
      onSuccess();
    } catch (_) {}
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:460, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Add Manual Credit</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>Requires admin approval before crediting</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Partner *</label>
            <select value={form.partner_id} onChange={e=>setForm(f=>({...f,partner_id:e.target.value}))} style={is}>
              <option value="">Select partner...</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.name} ({p.referral_partner_id})</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Commission Amount (₹) *</label>
            <input type="number" min={1} value={form.commission_amount} onChange={e=>setForm(f=>({...f,commission_amount:e.target.value}))} placeholder="e.g. 1000" style={is} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Reason *</label>
            <input type="text" value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Referral for Annual Filing — Pinaak Sarang" style={is} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Invoice Reference</label>
            <input type="text" value={form.invoice_ref} onChange={e=>setForm(f=>({...f,invoice_ref:e.target.value}))} placeholder="e.g. INV-26-27-001 (optional)" style={is} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes</label>
            <input type="text" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Any additional notes" style={is} />
          </div>
          <div style={{ background:darkMode?'rgba(139,92,246,0.1)':'#f5f3ff', border:`1px solid ${darkMode?'rgba(139,92,246,0.25)':'#ddd6fe'}`, borderRadius:10, padding:'10px 14px' }}>
            <p style={{ fontSize:12, color:'#7c3aed', fontWeight:600, margin:0 }}>⚠ Pending approval — Admin must approve before credits are added</p>
          </div>
        </div>
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving||!form.partner_id||!form.commission_amount||!form.reason}
            style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#8b5cf6,#7c3aed)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:(saving||!form.partner_id||!form.commission_amount||!form.reason)?0.6:1 }}>
            {saving?'Submitting...':'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Partner Modal ───────────────────────────────────────────────────────
function EditPartnerModal({ partner, onClose, onSuccess, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [form, setForm] = useState({
    name: partner.name || '',
    phone: partner.phone || '',
    email: partner.email || '',
    commission_type: partner.commission_type || 'percentage',
    commission_value: partner.commission_value || '',
    status: partner.status || 'active',
    notes: partner.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const is = { width:'100%', border:`1px solid ${T.inputBorder||T.border2}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg||T.bg2, color:T.text, boxSizing:'border-box' };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${BASE}/api/referral-partners/${partner.id}`, {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify(form),
      });
      onSuccess();
    } catch (_) {}
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:460, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Edit Partner</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{partner.referral_partner_id}</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Name</label>
              <input type="text" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Phone</label>
              <input type="text" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Email</label>
              <input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Commission Type</label>
              <select value={form.commission_type} onChange={e=>setForm(f=>({...f,commission_type:e.target.value}))} style={is}>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (₹)</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>
                {form.commission_type==='percentage'?'Rate (%)':'Amount (₹)'}
              </label>
              <input type="number" value={form.commission_value} onChange={e=>setForm(f=>({...f,commission_value:e.target.value}))} style={is} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Status</label>
              <select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} style={is}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase' }}>Notes</label>
              <input type="text" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={is} />
            </div>
          </div>
        </div>
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
            {saving?'Saving...':'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Convert External Partner to Client Modal ─────────────────────────────────
function ConvertToClientModal({ partner, onClose, onSuccess, darkMode, T, token }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleConvert = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${partner.id}/convert-to-client`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ partner_id: partner.id }),
      });
      if (res.ok) { setDone(true); setTimeout(onSuccess, 1500); }
      else { const d = await res.json(); showToast(d.message || 'Conversion failed', 'error'); }
    } catch (_) { showToast('Server error', 'error'); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:460, boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#16a34a,#15803d)', borderRadius:'20px 20px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Convert to Client</h3>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.7)', margin:'3px 0 0' }}>{partner.name} · {partner.referral_partner_id}</p>
          </div>
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding:'24px', background:T.bg3 }}>
          {done ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:'#f0fdf4', border:'2px solid #bbf7d0', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Converted Successfully!</p>
              <p style={{ fontSize:12, color:T.text3, marginTop:6 }}>{partner.name} is now a client. Referral credits preserved.</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ background:darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(22,163,74,0.25)':'#bbf7d0'}`, borderRadius:12, padding:16 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:'0 0 10px' }}>What happens when you convert:</p>
                {[
                  'A new Client profile is created with their name, phone, email',
                  'Partner type changes from External → Client Partner',
                  'All referral credits and commission history are preserved',
                  'They can now redeem credits against their own invoices',
                  'Their Referral Partner ID stays the same',
                ].map((item, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" style={{ flexShrink:0, marginTop:1 }}><polyline points="20 6 9 17 4 12"/></svg>
                    <p style={{ fontSize:12, color:T.text2, margin:0 }}>{item}</p>
                  </div>
                ))}
              </div>
              <div style={{ background:darkMode?'rgba(245,158,11,0.1)':'#fffbeb', border:`1px solid ${darkMode?'rgba(245,158,11,0.25)':'#fde68a'}`, borderRadius:10, padding:'10px 14px' }}>
                <p style={{ fontSize:12, color:'#d97706', margin:0, fontWeight:600 }}>⚠ You will need to complete the client profile (PAN, GSTIN etc.) in Clients module after conversion.</p>
              </div>
            </div>
          )}
        </div>
        {!done && (
          <div style={{ padding:'14px 24px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
            <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
            <button onClick={handleConvert} disabled={saving} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
              {saving && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving?'Converting...':'Confirm Convert'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReferralPartnersInner({ darkMode = false, T: theme }) {
  const T = theme || { bg: '#f4f7ff', bg2: '#fff', bg3: '#f8faff', border: '#e8edf5', border2: '#e2e8f0', text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8', cardBorder: '#f1f5f9', inputBg: '#fff', inputBorder: '#e2e8f0' };
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };
  const { showToast, showConfirm } = useToast();

  const [activeTab, setActiveTab] = useState('client');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewPartner, setViewPartner] = useState(null);
  const [redeemPartner, setRedeemPartner] = useState(null);
  const [encashPartner, setEncashPartner] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [editPartner, setEditPartner] = useState(null);
  const [convertPartner, setConvertPartner] = useState(null);

  const handleDeletePartner = async (p) => {
    if (Number(p.total_earned || 0) > 0) {
      showToast(`Cannot delete — ${p.name} has earned ₹${Number(p.total_earned).toLocaleString('en-IN')} in commissions. Deactivate instead.`, 'error');
      return;
    }
    if (!(await showConfirm(`Delete ${p.name}? This cannot be undone.`, { title: 'Delete Partner', confirmLabel: 'Delete', confirmColor: '#ef4444' }))) return;
    try {
      const res = await fetch(`${BASE}/api/referral-partners/${p.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { showToast(`${p.name} deleted`, 'success'); fetchPartners(); }
      else { const d = await res.json().catch(() => ({})); showToast(d.message || 'Could not delete partner', 'error'); }
    } catch (_) { showToast('Server error while deleting partner', 'error'); }
  };
  const [commissions, setCommissions] = useState([]);
  const [showCommissionModal, setShowCommissionModal] = useState(null);

  const fetchCommissions = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/referral-partners/commissions/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCommissions(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, [token]);

  useEffect(() => { fetchCommissions(); }, [fetchCommissions]);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners`, { headers: h });
      const data = await res.json();
      setPartners(Array.isArray(data) ? data : []);
    } catch (_) { setPartners([]); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const clientPartners = partners.filter(p => p.partner_type === 'client');
  const externalPartners = partners.filter(p => p.partner_type === 'external');

  const filtered = (activeTab === 'client' ? clientPartners : activeTab === 'external' ? externalPartners : partners)
    .filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.referral_partner_id?.toLowerCase().includes(search.toLowerCase()));

  const pendingCount = commissions.filter(c => c.status === 'pending').length;
  const TABS = [
    { id: 'client', label: `Client Partners (${clientPartners.length})` },
    { id: 'external', label: `External Partners (${externalPartners.length})` },
    { id: 'commissions', label: `Commissions${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
    { id: 'performance', label: 'Performance' },
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', padding: 24, background: darkMode ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),#0a0f1e' : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),#f4f7ff' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, margin: 0 }}>Referral Partners</h2>
          <p style={{ fontSize: 12, color: T.text3, margin: '4px 0 0' }}>{partners.length} partners · {fmt(partners.reduce((s, p) => s + (Number(p.credit_balance) || 0), 0))} total credits pending</p>
        </div>
        {activeTab === 'external' && (
          <button onClick={() => setShowAddModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Partner
          </button>
        )}
      </div>

      {/* Tabs + Search */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0, gap: 14 }}>
        <div style={{ display: 'flex', gap: 4, background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 5, boxShadow: darkMode ? '0 2px 12px rgba(0,0,0,0.3)' : '0 1px 4px rgba(15,31,69,0.06)' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                background: activeTab === tab.id ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
                color: activeTab === tab.id ? '#fff' : T.text3,
                boxShadow: activeTab === tab.id ? '0 2px 8px rgba(15,31,69,0.25)' : 'none',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab !== 'performance' && activeTab !== 'commissions' && (
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {/* List/Grid toggle */}
            <div style={{ display:'flex', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:9, padding:3 }}>
              <button onClick={() => setViewMode('list')} title="List View"
                style={{ width:30, height:26, borderRadius:7, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                  background: viewMode==='list'?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'transparent',
                  color: viewMode==='list'?'#fff':T.text3 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </button>
              <button onClick={() => setViewMode('grid')} title="Grid View"
                style={{ width:30, height:26, borderRadius:7, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                  background: viewMode==='grid'?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'transparent',
                  color: viewMode==='grid'?'#fff':T.text3 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              </button>
            </div>
          </div>
        )}
        {activeTab !== 'performance' && activeTab !== 'commissions' && (
          <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search partners..." style={{ width: '100%', border: `1px solid ${T.border2}`, borderRadius: 10, padding: '9px 12px 9px 32px', fontSize: 13, outline: 'none', background: T.bg2, color: T.text, boxSizing: 'border-box' }} />
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none' }}>
        {activeTab === 'performance' ? (
          <PerformanceTab partners={partners} darkMode={darkMode} T={T} />
        ) : activeTab === 'commissions' ? (
          <CommissionsTab
            commissions={commissions}
            partners={partners}
            onRefresh={() => { fetchCommissions(); fetchPartners(); }}
            onManualAdd={(p) => setShowCommissionModal(p)}
            darkMode={darkMode}
            T={T}
            token={token}
          />
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.text3, fontSize: 13 }}>Loading partners...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: T.bg2, border: `2px dashed ${T.border2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: T.text, margin: 0 }}>
              {activeTab === 'client' ? 'No client partners yet' : 'No external partners yet'}
            </p>
            <p style={{ fontSize: 12, color: T.text3, marginTop: 6 }}>
              {activeTab === 'client' ? 'Enable referral toggle on a client profile to add them here' : 'Click Add Partner to add an external referral partner'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {filtered.map(partner => (
              <PartnerCard key={partner.id} partner={partner} onView={setViewPartner} onRedeem={setRedeemPartner} onEncash={setEncashPartner} darkMode={darkMode} T={T} />
            ))}
          </div>
        ) : (
          /* LIST VIEW */
          <div style={{ background:T.bg2, borderRadius:16, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow:darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  {['Partner','Type','Commission','Ledger Balance','Credits Earned','Total Earned','Leads','Actions'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding:'48px 16px', textAlign:'center', color:T.text3, fontSize:13 }}>No partners found</td></tr>
                ) : filtered.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom:`1px solid ${T.border}`, background:darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:9, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:700, flexShrink:0 }}>
                          {(p.name||'P').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{p.name}</p>
                          <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{p.referral_partner_id}</p>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <Badge label={p.partner_type==='client'?'Client':'External'} color={p.partner_type==='client'?'#3b82f6':'#8b5cf6'} bg={p.partner_type==='client'?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):(darkMode?'rgba(139,92,246,0.12)':'#f5f3ff')} />
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:12, color:T.text2 }}>
                      {p.commission_type==='percentage'?`${Number(p.commission_value||0)}%`:fmt(p.commission_value)}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:(p.ledger_outstanding||0)>0?'#dc2626':'#16a34a' }}>
                      {p.partner_type==='client'?fmt(p.ledger_outstanding||0):'—'}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:700, color:'#16a34a' }}>{fmt(p.credit_balance||0)}</td>
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:'#3b82f6' }}>{fmt(p.total_earned||0)}</td>
                    <td style={{ padding:'12px 16px', fontSize:13, color:T.text2 }}>
                      <span style={{ fontWeight:600, color:'#8b5cf6' }}>{fmtN(p.total_leads||0)}</span>
                      <span style={{ fontSize:11, color:T.text3 }}> / {fmtN(p.converted_leads||0)} conv.</span>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                        <button onClick={()=>setViewPartner(p)} style={{ padding:'5px 10px', borderRadius:7, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12, fontWeight:500, cursor:'pointer' }}
                          onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                          onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Leads</button>
                        {p.partner_type==='client' && (p.credit_balance||0)>0 && (
                          <button onClick={()=>setRedeemPartner(p)} style={{ padding:'5px 10px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff', fontSize:12, fontWeight:500, cursor:'pointer' }}>Redeem</button>
                        )}
                        {(p.credit_balance||0)>0 && (
                          <button onClick={()=>setEncashPartner(p)} style={{ padding:'5px 10px', borderRadius:7, border:'none', background:'linear-gradient(135deg,#f97316,#ea580c)', color:'#fff', fontSize:12, fontWeight:500, cursor:'pointer' }}>Encash</button>
                        )}
                        {/* Edit */}
                        <button onClick={()=>setEditPartner(p)} title="Edit Partner"
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                          onMouseEnter={e=>{e.currentTarget.style.background=T.bg3;e.currentTarget.style.color='#f97316';e.currentTarget.style.borderColor='#f97316';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=T.text3;e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {/* Convert to Client — only for external */}
                        {p.partner_type==='external' && (
                          <button onClick={()=>setConvertPartner(p)} title="Convert to Client"
                            style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                            onMouseEnter={e=>{e.currentTarget.style.background=T.bg3;e.currentTarget.style.color='#16a34a';e.currentTarget.style.borderColor='#16a34a';}}
                            onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=T.text3;e.currentTarget.style.borderColor=T.border2;}}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                          </button>
                        )}
                        {/* Delete */}
                        <button onClick={()=>handleDeletePartner(p)} title={Number(p.total_earned||0)>0?'Cannot delete — has commission history':'Delete Partner'}
                          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.border2}`, background:'none', color:Number(p.total_earned||0)>0?T.text3:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor:Number(p.total_earned||0)>0?'not-allowed':'pointer', opacity:Number(p.total_earned||0)>0?0.4:1 }}
                          onMouseEnter={e=>{ if(Number(p.total_earned||0)===0){ e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'; e.currentTarget.style.borderColor='#ef4444'; }}}
                          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.borderColor=T.border2;}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {viewPartner && <PartnerLeadsModal partner={viewPartner} onClose={() => setViewPartner(null)} darkMode={darkMode} T={T} />}
      {redeemPartner && <RedeemModal partner={redeemPartner} onClose={() => setRedeemPartner(null)} onSuccess={fetchPartners} darkMode={darkMode} T={T} />}
      {encashPartner && <EncashModal partner={encashPartner} onClose={() => setEncashPartner(null)} onSuccess={fetchPartners} darkMode={darkMode} T={T} />}
      {showAddModal && <AddPartnerModal onClose={() => setShowAddModal(false)} onSuccess={fetchPartners} darkMode={darkMode} T={T} />}
      {editPartner && <EditPartnerModal partner={editPartner} onClose={() => setEditPartner(null)} onSuccess={() => { setEditPartner(null); fetchPartners(); }} darkMode={darkMode} T={T} token={token} />}
      {convertPartner && <ConvertToClientModal partner={convertPartner} onClose={() => setConvertPartner(null)} onSuccess={() => { setConvertPartner(null); fetchPartners(); }} darkMode={darkMode} T={T} token={token} />}
      {showCommissionModal && (
        <ManualCommissionModal
          partner={showCommissionModal}
          onClose={() => setShowCommissionModal(null)}
          onSuccess={() => { fetchCommissions(); fetchPartners(); setShowCommissionModal(null); }}
          darkMode={darkMode}
          T={T}
          token={token}
        />
      )}
    </div>
  );
}

export default function ReferralPartners(props) {
  return (
    <ToastProvider>
      <ReferralPartnersInner {...props} />
    </ToastProvider>
  );
}
